<template>
  <div class="multiselect">
    <dropdown
      flex
      :align="align"
      :active="active"
      @close="active = false"
    >
      <div :class="['input', { active }]" @click="active = true">
        <label>{{ label }}</label>

        <div class="values">
          <div
            class="chip"
            v-for="(item, i) in value"
            :key="i"
            @click.stop="removeItem(i)"
          >
            <span>{{ item.label }}</span>

            <icon name="cancel" />
          </div>

          <input
            ref="input"
            v-model="searchString"
            @focus="onInputFocus"
            @blur="onInputBlur"
            @keydown="navigateItems"
          />
        </div>

        <icon :name="active ? 'expand_less' : 'expand_more'" class="arrow" />
      </div>

      <template #menu>
        <dropdown-menu
          :options="filteredOptions"
          :focus="itemFocus"
          @select="handleSelect"
          @mouseenter="mouseover = 'menu'"
          @mouseleave="mouseover = ''"
        />
      </template>
    </dropdown>
  </div>
</template>

<script>
import Dropdown from './Dropdown';
import DropdownMenu from './DropdownMenu';
import Icon from './Icon';

export default {
  components: {
    Dropdown,
    DropdownMenu,
    Icon,
  },

  props: {
    value: Array,
    options: Array,
    label: String,
    align: {
      type: String,
      default: 'left',
    },
  },

  data() {
    return {
      active: false,
      searchString: '',
      itemFocus: null,
      mouseover: false,
    };
  },

  watch: {
    active(value) {
      if (value) {
        this.$refs.input.focus();
      } else {
        this.searchString = '';
        this.$refs.input.blur();
      }
    },
  },

  computed: {
    filteredOptions() {
      const values = this.value.map((item) => item?.value);

      return this.options.filter((item) => {
        return !values.includes(item.value)
          && item.label.toLowerCase().indexOf(this.searchString.toLowerCase()) > -1;
      });
    },
  },

  methods: {
    handleSelect(selectedValue) {
      const item = this.options.find(({ value }) => value === selectedValue);
      const values = [...this.value, item];

      this.$emit('input', values);
      this.searchString = '';
      this.$refs.input.focus();
    },

    removeItem(index) {
      const values = [...this.value];

      values.splice(index, 1);
      this.$emit('input', values);
    },

    navigateItems(e) {
      try {
        switch (e.keyCode) {
          case 8:
            if (!this.searchString) {
              this.removeItem(this.value.length - 1);
            }
            break;
          case 13:
            this.handleSelect(this.filteredOptions[this.itemFocus || 0].value);
            this.itemFocus = null;
            break;
          case 38:
            if (this.itemFocus === null) {
              this.itemFocus = 0;
            } else if (this.itemFocus > 0) {
              this.itemFocus--;
            }
            break;
          case 40:
            if (this.itemFocus === null) {
              this.itemFocus = 0;
            } else if (this.itemFocus < this.filteredOptions.length - 1) {
              this.itemFocus++;
            }
            break;
        }
      } catch (e) {
        // swallow
      }
    },

    onInputFocus() {
      this.active = true;
    },

    onInputBlur() {
      if (this.mouseover === 'menu') return;

      this.active = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.multiselect {
  margin-bottom: var(--multiselect-margin-bottom, $gp);
  position: relative;
}

.dropdown {
  width: 100%;
}

.input {
  background-color: var(--theme-background);
  border: solid 1px var(--theme-input-border);
  border-radius: $border-radius;
  width: 100%;
  min-height: 46px;
  padding: calc(#{$gp * .5} - 2px);
  display: flex;
  align-items: center;

  .arrow {
    font-size: 24px;
    color: $color-gray-5;
  }

  &.active {
    border-color: $color-green-1;
  }

  label {
    color: $color-gray-5;
    background-color: var(--theme-background);
    font-size: 12px;
    position: absolute;
    top: -8px;
    left: $gp * .75;
    font-weight: bold;
    padding: 0 3px;
  }
}

.values {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
}

.chip {
  background-color: var(--theme-pill);
  height: 24px;
  padding: 0 $gp * .25 0 $gp * .5;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  margin: 2px;
  font-size: 14px;

  &:hover {
    background-color: var(--theme-pill-hover);
  }

  .icon {
    font-size: 16px;
    margin-left: $gp * .25;
  }
}

input {
  border: none;
  background-color: transparent;
  flex: 1;
  min-width: 100px;
}
</style>
